<script setup>
import { computed, ref, watch } from 'vue'
import { Button, Drawer, Spinner } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { getLlmConversation } from '../../../api/llm/index.js'
import ConversationRound from './ConversationRound.vue'

const props = defineProps({
  open: { type: Boolean, required: true },
  runId: { type: String, required: true },
  conversationKey: { type: String, required: true },
  turnId: { type: String, required: true },
})

const emit = defineEmits(['update:open'])
const { t } = useI18n()
const router = useRouter()

/** @type {import('vue').Ref<import('../../../api/llm/conversations.js').ConversationDetail|null>} */
const detail = ref(null)
const loading = ref(false)
const error = ref('')
const title = computed(() => t('visualGameAdmin.llm.conversations.detail.title'))

watch(
  () => [props.open, props.runId, props.conversationKey],
  async ([open]) => {
    if (!open) return
    await load()
  },
)

async function load() {
  loading.value = true
  error.value = ''
  detail.value = null
  try {
    detail.value = await getLlmConversation(props.runId, props.conversationKey)
  } catch (err) {
    error.value = err.message
  } finally {
    loading.value = false
  }
}

function roleLabel(role) {
  switch (role) {
    case 'kp':
    case 'npc':
    case 'npc_action_kp':
      return t(`visualGameAdmin.llm.conversations.roles.${role}`)
    default:
      throw new Error(`Unhandled conversation agent role: ${role}`)
  }
}

function openRunVideoMatch() {
  router.push({ name: 'llm-run-video-match', params: { runId: props.runId } })
}
</script>

<template>
  <Drawer :open="open" :title="title" width="min(960px, 94vw)" @update:open="emit('update:open', $event)">
    <div v-if="loading" class="conversation-detail__state">
      <Spinner size="lg" :label="t('visualGameAdmin.llm.conversations.detail.loading')" />
    </div>

    <div v-else-if="error" class="conversation-detail__state">
      <p class="vga-error" role="alert">{{ error }}</p>
      <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>

    <div v-else-if="detail" class="conversation-detail">
      <header class="conversation-detail__summary">
        <div>
          <strong>
            {{ t('visualGameAdmin.llm.conversations.detail.summary', {
              scenario: detail.conversation.scenario_title,
              role: roleLabel(detail.conversation.agent_role),
              turns: detail.rounds.length,
              calls: detail.conversation.call_count,
            }) }}
          </strong>
          <span class="vga-mono">{{ detail.conversation.run_id }} · {{ detail.conversation.conversation_key }}</span>
        </div>
        <Button variant="secondary" size="sm" @click="openRunVideoMatch">
          {{ t('visualGameAdmin.llm.runVideoMatch.openRun') }}
        </Button>
      </header>

      <h3>{{ t('visualGameAdmin.llm.conversations.detail.roundsTitle') }}</h3>
      <ol class="conversation-detail__rounds">
        <ConversationRound
          v-for="round in detail.rounds"
          :key="round.turn_id"
          :round="round"
          :run-id="runId"
          :conversation-key="conversationKey"
          :targeted="round.turn_id === turnId"
        />
      </ol>
    </div>

    <template #footer>
      <Button variant="secondary" @click="emit('update:open', false)">{{ t('visualGameAdmin.common.close') }}</Button>
    </template>
  </Drawer>
</template>

<style scoped>
.conversation-detail,
.conversation-detail__state {
  min-width: 0;
}

.conversation-detail__state {
  min-height: 240px;
  display: grid;
  place-items: center;
  align-content: center;
  gap: var(--drama-space-lg);
}

.conversation-detail__summary {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--drama-space-xs) var(--drama-space-lg);
  margin-bottom: var(--drama-space-lg);
  padding: var(--drama-space-md);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-lg);
  background: var(--drama-color-surface-alt);
}

.conversation-detail__summary span {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
  overflow-wrap: anywhere;
}

.conversation-detail__summary > div {
  display: grid;
  gap: var(--drama-space-xs);
  min-width: 0;
}

.conversation-detail h3 {
  margin: 0 0 var(--drama-space-sm);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
  font-weight: var(--drama-font-weight-medium);
}

.conversation-detail__rounds {
  display: grid;
  gap: var(--drama-space-md);
  margin: 0;
  padding: 0;
  list-style: none;
}
</style>
